import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Plus } from "@phosphor-icons/react";
import api, { apiError } from "../../lib/api";
import { inr, pct, gainClass, kycBadge, riskBadge, clientStatusBadge } from "../../lib/format";
import AppLayout, { PageLoader, EmptyState, thCls, tdCls, rowCls } from "../../components/AppLayout";
import { Badge } from "../../components/ui/badge";
import { Button } from "../../components/ui/button";

export default function AdminClients() {
  const [rows, setRows] = useState(null);
  const [error, setError] = useState("");
  const [query, setQuery] = useState("");
  const navigate = useNavigate();

  useEffect(() => {
    api.get("/admin/investors").then((r) => setRows(r.data)).catch((e) => setError(apiError(e)));
  }, []);

  if (error) return <AppLayout title="Clients"><p className="text-red-400 text-sm">{error}</p></AppLayout>;
  if (!rows) return <AppLayout title="Clients"><PageLoader /></AppLayout>;

  const filtered = rows.filter((r) =>
    r.full_name.toLowerCase().includes(query.toLowerCase()) ||
    r.investor_code.toLowerCase().includes(query.toLowerCase()));

  return (
    <AppLayout title="Clients" subtitle={`${rows.length} master client records`}>
      <div className="fade-up space-y-5">
        <div className="flex flex-wrap items-center justify-between gap-3">
          <input data-testid="client-search" placeholder="Search by name or client ID..." value={query}
            onChange={(e) => setQuery(e.target.value)} className="glass-input max-w-xs" />
          <Button data-testid="add-client-button" onClick={() => navigate("/admin/clients/new")}
            className="bg-emerald-500 hover:bg-emerald-400 text-[#030706] font-bold emerald-glow">
            <Plus size={16} className="mr-1.5" weight="bold" /> Add New Client
          </Button>
        </div>

        <div className="glass-card overflow-hidden">
          {filtered.length === 0 ? <EmptyState message="No clients match your search." /> : (
            <div className="overflow-x-auto">
              <table className="w-full text-sm" data-testid="admin-clients-table">
                <thead>
                  <tr className="border-b border-white/[0.06]">
                    <th className={thCls}>Client</th>
                    <th className={thCls}>ID</th>
                    <th className={thCls}>Risk</th>
                    <th className={thCls}>WRM</th>
                    <th className={thCls}>KYC</th>
                    <th className={thCls}>Status</th>
                    <th className={`${thCls} text-right`}>AUM</th>
                    <th className={`${thCls} text-right`}>Return</th>
                  </tr>
                </thead>
                <tbody>
                  {filtered.map((r) => (
                    <tr key={r.id} onClick={() => navigate(`/admin/clients/${r.id}`)} data-testid={`client-row-${r.id}`}
                      className={`${rowCls} cursor-pointer`}>
                      <td className={tdCls}>
                        <p className="font-medium text-white">{r.full_name}</p>
                        <p className="text-[11px] text-zinc-500">{r.email}</p>
                      </td>
                      <td className={`${tdCls} tnum text-xs text-zinc-400`}>{r.investor_code}</td>
                      <td className={tdCls}>
                        <Badge variant="outline" className={`text-[10px] ${riskBadge(r.risk_profile)}`}>{r.risk_profile}</Badge>
                      </td>
                      <td className={`${tdCls} text-xs text-zinc-400`}>{r.wrm_name || "—"}</td>
                      <td className={tdCls}>
                        <Badge variant="outline" className={`text-[10px] ${kycBadge(r.kyc_status)}`}>{r.kyc_status}</Badge>
                      </td>
                      <td className={tdCls}>
                        <Badge variant="outline" className={`text-[10px] ${clientStatusBadge(r.client_status)}`}>{r.client_status || "Active"}</Badge>
                      </td>
                      <td className={`${tdCls} text-right tnum text-zinc-100`}>{inr(r.aum)}</td>
                      <td className={`${tdCls} text-right tnum font-semibold ${gainClass(r.gain_loss_pct)}`}>{pct(r.gain_loss_pct)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </div>
    </AppLayout>
  );
}
